import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
    Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { designTokens } from '../theme/designTokens';

const CustomModal = ({
    open = false,
    onClose,
    title,
    subtitle,
    icon,
    children,
    actions,
    maxWidth = 'lg',
    fullWidth = true,
    disableBackdropClick = false,
    showCloseIcon = true,
    contentPadding = true,
    actionsAlignment = 'flex-end',
    ...dialogProps
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const handleClose = (event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return;
        }
        if (onClose) {
            onClose(event, reason);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            fullWidth={fullWidth}
            maxWidth={maxWidth}
            TransitionComponent={Fade}
            transitionDuration={300}
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: designTokens.borderRadius.sm,
                    boxShadow: theme.palette.mode === 'dark'
                        ? designTokens.shadows.xl
                        : designTokens.shadows.lg,
                    border: `1px solid ${theme.palette.mode === 'dark'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[200]}`,
                    backgroundImage: 'none',
                    backgroundColor: theme.palette.background.paper,
                    margin: fullScreen ? 0 : designTokens.spacing['2xl'],
                    maxHeight: fullScreen ? '100vh' : 'calc(100vh - 96px)',
                    overflow: 'hidden',
                },
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(0, 0, 0, 0.8)'
                        : 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(8px)',
                },
            }}
            {...dialogProps}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    position: 'relative',
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: 'none',
                    borderTopLeftRadius: designTokens.borderRadius.sm,
                    borderTopRightRadius: designTokens.borderRadius.sm,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {icon && (
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: designTokens.borderRadius.sm,
                                backgroundColor: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.5
                            }}>
                                {icon}
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: designTokens.fontWeight.semibold,
                                    fontSize: designTokens.fontSize.xl,
                                    color: theme.palette.text.primary,
                                    mb: subtitle ? 1 : 0,
                                    lineHeight: designTokens.lineHeight.tight,
                                }}
                            >
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: designTokens.fontSize.sm,
                                        fontWeight: designTokens.fontWeight.normal,
                                        lineHeight: designTokens.lineHeight.normal,
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {showCloseIcon && (
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: theme.palette.grey[400],
                                backgroundColor: 'transparent',
                                borderRadius: designTokens.borderRadius.sm,
                                width: 28,
                                height: 28,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? theme.palette.grey[800]
                                        : theme.palette.grey[100],
                                    color: theme.palette.grey[600],
                                },
                                transition: 'all 0.15s ease-in-out',
                                mt: 0.5
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent
                sx={{
                    minHeight: 'min(550px, calc(100vh - 300px))',
                    padding: contentPadding
                        ? `0 ${designTokens.spacing['2xl']}px ${designTokens.spacing.lg}px`
                        : 0,
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.grey[600]
                            : theme.palette.grey[300],
                        borderRadius: '2px',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? theme.palette.grey[500]
                                : theme.palette.grey[400],
                        },
                    },
                }}
            >
                {children}
            </DialogContent>

            {/* Actions */}
            {actions && (
                <DialogActions
                    sx={{
                        borderTop: `1px solid ${theme.palette.mode === 'dark'
                            ? theme.palette.grey[700]
                            : theme.palette.grey[100]}`,
                        backgroundColor: theme.palette.background.paper,
                        borderBottomLeftRadius: designTokens.borderRadius.sm,
                        borderBottomRightRadius: designTokens.borderRadius.sm,
                        justifyContent: actionsAlignment,
                        flexWrap: 'wrap',
                    }}
                >
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default CustomModal;